import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Table } from 'reactstrap';
import { Translate, TextFormat, getPaginationState, JhiPagination, JhiItemCount } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import { APP_DATE_FORMAT, APP_LOCAL_DATE_FORMAT, AUTHORITIES } from 'app/config/constants';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities } from './device-payload.reducer';
import { isArray } from 'lodash';

export const DevicePayload = () => {
  const dispatch = useAppDispatch();
  const applicationUser = useAppSelector(state => state.authentication?.internalUser);
  const authorities = useAppSelector(state => state.authentication.account.authorities);

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id', 'desc'), pageLocation.search),
  );

  const devicePayloadList = useAppSelector(state => state.devicePayload.entities);
  const loading = useAppSelector(state => state.devicePayload.loading);
  const totalItems = useAppSelector(state => state.devicePayload.totalItems);

  const getAllEntities = () => {
    let filterUrl = '';
    if (authorities.some(r => [AUTHORITIES.UTILITY_ADMIN, AUTHORITIES.UTILITY_USER].includes(r))) {
      filterUrl = `orgId.equals=${applicationUser?.organization?.id}&`;
    }
    dispatch(
      getEntities({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
        filterUrl: applicationUser?.organization?.id ? `orgId.equals=${applicationUser?.organization?.id}&` : '',
      }),
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    if (!applicationUser) {
      return;
    }
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort, applicationUser]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    } else {
      return order === ASC ? faSortUp : faSortDown;
    }
  };

  return (
    <div>
      <h2 id="device-payload-heading" data-cy="DevicePayloadHeading">
        <Translate contentKey="rainspotApp.devicePayload.home.title">Device Payloads</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="rainspotApp.devicePayload.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link to="/device-payload/new" className="btn btn-primary jh-create-entity" id="jh-create-entity" data-cy="entityCreateButton">
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="rainspotApp.devicePayload.home.createLabel">Create new Device Payload</Translate>
          </Link>
        </div>
      </h2>
      <div className="table-responsive">
        {isArray(devicePayloadList) && devicePayloadList?.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="rainspotApp.devicePayload.id">ID</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('attribute')}>
                  <Translate contentKey="rainspotApp.devicePayload.attribute">Attribute</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('attribute')} />
                </th>
                <th className="hand" onClick={sort('direction')}>
                  <Translate contentKey="rainspotApp.devicePayload.direction">Direction</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('direction')} />
                </th>
                <th className="hand" onClick={sort('value')}>
                  <Translate contentKey="rainspotApp.devicePayload.value">Value</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('value')} />
                </th>
                <th className="hand" onClick={sort('timeOfOrigin')}>
                  <Translate contentKey="rainspotApp.devicePayload.timeOfOrigin">Time Of Origin</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('timeOfOrigin')} />
                </th>
                <th className="hand" onClick={sort('timeOfIngestion')}>
                  <Translate contentKey="rainspotApp.devicePayload.timeOfIngestion">Time Of Ingestion</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('timeOfIngestion')} />
                </th>
                <th>
                  <Translate contentKey="rainspotApp.devicePayload.device">Device</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {devicePayloadList.map((devicePayload, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/device/${devicePayload.deviceId}/edit`} color="link" size="sm">
                      {devicePayload?.id}
                    </Button>
                  </td>
                  <td>{devicePayload?.attribute}</td>
                  <td>
                    <Translate contentKey={`rainspotApp.PayloadDirection.${devicePayload.direction}`} />
                  </td>
                  <td title={devicePayload?.value?.map(v => JSON.stringify(v))?.join(',')} style={{ maxWidth: '300px' }}>
                    {devicePayload?.value?.map(v => JSON.stringify(v))?.join(',')}
                  </td>
                  <td>
                    {devicePayload?.timeOfOrigin ? (
                      <TextFormat type="date" value={devicePayload?.timeOfOrigin} format={APP_DATE_FORMAT} />
                    ) : null}
                  </td>
                  <td>
                    {devicePayload.timeOfIngestion ? (
                      <TextFormat type="date" value={devicePayload?.timeOfIngestion} format={APP_DATE_FORMAT} />
                    ) : null}
                  </td>
                  <td>
                    {devicePayload.deviceId ? (
                      <Button
                        style={{
                          maxWidth: '180px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                        tag={Link}
                        to={`/device/${devicePayload.deviceId}/edit`}
                        color="link"
                        size="sm"
                        title={devicePayload?.serialNo}
                      >
                        {devicePayload?.serialNo}
                      </Button>
                    ) : (
                      devicePayload?.serialNo
                    )}
                  </td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button tag={Link} to={`/device-payload/${devicePayload.id}`} color="info" size="sm" data-cy="entityDetailsButton">
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/device-payload/${devicePayload.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() =>
                          (window.location.href = `/device-payload/${devicePayload.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)
                        }
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="rainspotApp.devicePayload.home.notFound">No Device Payloads found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={devicePayloadList && devicePayloadList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default DevicePayload;
